import { useState } from 'react';
import config from '../config/config';
import { useAuth } from '../contexts/AuthContext';
import authHeader from '../config/authHeader';

const useFetchDommages = () => {
    const { token, logout } = useAuth();

    const [dommages, setDommages] = useState([]);
    const [countDommages, setCountDommages] = useState(0);
    const [dommage, setDommage] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const getHeaders = () => ({
        Authorization: `Bearer ${token}`
    });

    const fetchDommages = async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await fetch(`${config.apiBaseUrl}/4DACTION/KST_getDommages`, {
                method: 'GET',
                headers: authHeader(),
            });

            if (response.status === 401) return logout();
            if (!response.ok) throw new Error('Erreur réseau lors de la récupération des Dommages');

            const data = await response.json();
            setDommages(data);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const fetchCountDommages = async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await fetch(`${config.apiBaseUrl}/4DACTION/KST_getCountDommages`, {
                method: 'GET',
                headers: authHeader(),
            });

            if (response.status === 401) return logout();
            if (!response.ok) throw new Error('Erreur réseau lors de la récupération du compteur des Dommages');

            const { count } = await response.json();
            setCountDommages(count);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const fetchDommageByUUID = async (uuid) => {
        setLoading(true);
        setError(null);
        try {
            const response = await fetch(`${config.apiBaseUrl}/4DACTION/KST_getDommageByUUID?uuid=${uuid}`, {
                method: 'GET',
                headers: authHeader(),
            });

            if (response.status === 401) return logout();
            if (!response.ok) throw new Error('Erreur réseau lors de la récupération du Dommage par UUID');

            const data = await response.json();
            setDommage(data[0]);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const fetchDommagesByDeclaUUID = async (declaUUID) => {
        setLoading(true);
        setError(null);
        try {
            const response = await fetch(`${config.apiBaseUrl}/4DACTION/KST_getDommagesByDeclaUUID?uuid=${declaUUID}`, {
                method: 'GET',
                headers: authHeader(),
            });

            if (response.status === 401) return logout();
            if (!response.ok) {
                const errorResponse = await response.json();
                throw new Error(errorResponse.error || 'Erreur réseau pour récupérer les dommages par DeclUUID');
            }

            const data = await response.json();
            setDommages(data);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const saveDommageUpdate = async (dommage) => {
        const formData = new FormData();
        Object.entries(dommage).forEach(([key, value]) => {
            formData.append(key, value);
        });

        try {
            const response = await fetch(`${config.apiBaseUrl}/4DACTION/KST_editDommage`, {
                method: 'POST',
                headers: authHeader(),
                body: formData,
            });

            if (response.status === 401) return logout();
            if (!response.ok) {
                const errorResponse = await response.json();
                throw new Error(errorResponse.error || 'Erreur réseau pour sauvegarder le dommage');
            }

            const result = await response.json();
            console.log("Dommage mis à jour avec succès :", result);
        } catch (error) {
            console.error("Erreur lors de la mise à jour du dommage :", error);
            alert("Une erreur est survenue lors de l'enregistrement.");
        }
    };

    const createDommage = async (dommageData) => {
        setLoading(true);
        setError(null);

        const formData = new FormData();
        formData.append("dommagePayload", JSON.stringify(dommageData));

        try {
            const response = await fetch(`${config.apiBaseUrl}/4DACTION/KST_createDommage`, {
                method: 'POST',
                headers: authHeader(),
                body: formData,
            });

            if (response.status === 401) {
                logout();
                return null;
            }

            if (!response.ok) {
                throw new Error('Erreur réseau lors de la création de la Dommage');
            }

            const data = await response.json();
            return data;
        } catch (err) {
            setError(err.message);
            return null;
        } finally {
            setLoading(false);
        }
    };

    const updateDommage = async (dommageData) => {
        setLoading(true);
        setError(null);

        const formData = new FormData();
        formData.append("dommagePayload", JSON.stringify(dommageData));

        try {
            const response = await fetch(`${config.apiBaseUrl}/4DACTION/KST_updateDommage`, {
                method: 'POST',
                headers: authHeader(),
                body: formData,
            });

            if (response.status === 401) {
                logout();
                return null;
            }

            if (!response.ok) {
                const message = `Erreur (${response.status}) lors de la mise à jour du dommage`;

                const err = new Error(message);
                err.status = response.status;

                throw err;
            }

            const data = await response.json();
            return data;
        } catch (err) {
            setError(err.message);
            throw err;
        } finally {
            setLoading(false);
        }
    };

    return {
        dommages,
        countDommages,
        dommage,
        fetchDommages,
        fetchCountDommages,
        fetchDommageByUUID,
        fetchDommagesByDeclaUUID,
        saveDommageUpdate,
        updateDommage,
        createDommage,
        loading,
        error
    };
};

export default useFetchDommages;
